import React, { ReactElement, useContext } from 'react'

import Heading from '../../Common/Components/Heading'
import P from '../../Common/Components/P'
import Fieldset from '../../Common/Components/Fieldset'
import Checkbox from '../../Common/Components/Checkbox'
import TextInput from '../../Common/Components/TextInput'
import FormRow from '../../Common/Components/FormRow'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { InboundStatSettings as InboundStatSettingsLanguage } from '../../../../i18n'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'

/**
 * Component properties type.
 */
type Props = {
    isInbStIgnQuickAbndCallsWithinEnabled: boolean
    setInbStIgnQuickAbndCallsWithinEnabled: (checked: boolean) => void
    inbStIgnQuickAbndCallsWithin: number | undefined
    setInbStIgnQuickAbndCallsWithin: (newValue: number | undefined) => void
    isInbStIgnOvrflwdCallsWithinEnabled: boolean
    setInbStIgnOvrflwdCallsWithinEnabled: (checked: boolean) => void
    inbStIgnOvrflwdCallsWithin: number | undefined
    setInbStIgnOvrflwdCallsWithin: (newValue: number | undefined) => void
    isInboundEscapedCallsIgnored: boolean
    setIsInboundEscapedCallsIgnored: (newValue: boolean) => void
}

/**
 * Inbound statistic settings section of SLA/KPI tab in report configuration modal window.
 *
 * @param isInbStIgnQuickAbndCallsWithinEnabled Whether the input for corresponding property is enabled.
 * @param setInbStIgnQuickAbndCallsWithinEnabled Setter to enable corresponding property input.
 * @param inbStIgnQuickAbndCallsWithin Report property.
 * @param setInbStIgnQuickAbndCallsWithin Setter for corresponding report property.
 * @param isInbStIgnOvrflwdCallsWithinEnabled Whether the input for corresponding property is enabled.
 * @param setInbStIgnOvrflwdCallsWithinEnabled Setter to enable corresponding property input.
 * @param inbStIgnOvrflwdCallsWithin Report property.
 * @param setInbStIgnOvrflwdCallsWithin Setter for corresponding report property.
 * @param isInboundEscapedCallsIgnored Report property.
 * @param setIsInboundEscapedCallsIgnored Setter for corresponding report property.
 */
const InboundStatSettings: React.FC<Props> = ({
    isInbStIgnQuickAbndCallsWithinEnabled,
    setInbStIgnQuickAbndCallsWithinEnabled,
    inbStIgnQuickAbndCallsWithin,
    setInbStIgnQuickAbndCallsWithin,
    isInbStIgnOvrflwdCallsWithinEnabled,
    setInbStIgnOvrflwdCallsWithinEnabled,
    inbStIgnOvrflwdCallsWithin,
    setInbStIgnOvrflwdCallsWithin,
    isInboundEscapedCallsIgnored,
    setIsInboundEscapedCallsIgnored
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(InboundStatSettingsLanguage.NAME)

    return (
        <Fieldset className={'inbound-stat-settings'}>
            <Heading level={HeadingLevel.ONE}>
                {language.get(InboundStatSettingsLanguage.InboundStatSettings_SectionHeader)}
            </Heading>
            <P>{language.get(InboundStatSettingsLanguage.InboundStatSettings_SectionPara)}</P>
            <FormRow isInline className={'inbound-ignore-quickly-abandoned-calls'}>
                <Checkbox
                    labelLeft={language.get(
                        InboundStatSettingsLanguage.InboundStatSettings_IgnorequickabndcallswithinEnabledHiddenlabel
                    )}
                    isLabelHidden
                    onChange={setInbStIgnQuickAbndCallsWithinEnabled}
                    isChecked={isInbStIgnQuickAbndCallsWithinEnabled}
                />
                <TextInput
                    value={inbStIgnQuickAbndCallsWithin}
                    onChange={(value: string): void =>
                        setInbStIgnQuickAbndCallsWithin(parseInt(value, 10) || undefined)
                    }
                    labelLeft={language.get(
                        InboundStatSettingsLanguage.InboundStatSettings_IgnorequickabndcallswithinLabel
                    )}
                    placeholder={language.get(
                        InboundStatSettingsLanguage.InboundStatSettings_IgnorequickabndcallswithinPlaceholder
                    )}
                    disabled={!isInbStIgnQuickAbndCallsWithinEnabled}
                    adornmentEnd={language.get(
                        InboundStatSettingsLanguage.InboundStatSettings_IgnorequickabndcallswithinUnit
                    )}
                />
            </FormRow>
            <FormRow isInline className={'inbound-overflowed-calls'}>
                <Checkbox
                    labelLeft={language.get(
                        InboundStatSettingsLanguage.InboundStatSettings_IgnoreovrflwdcallswithinEnabledHiddenlabel
                    )}
                    isLabelHidden
                    onChange={setInbStIgnOvrflwdCallsWithinEnabled}
                    isChecked={isInbStIgnOvrflwdCallsWithinEnabled}
                />
                <TextInput
                    value={inbStIgnOvrflwdCallsWithin}
                    onChange={(value: string): void => setInbStIgnOvrflwdCallsWithin(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(
                        InboundStatSettingsLanguage.InboundStatSettings_IgnoreovrflwdcallswithinLabel
                    )}
                    placeholder={language.get(
                        InboundStatSettingsLanguage.InboundStatSettings_IgnoreovrflwdcallswithinPlaceholder
                    )}
                    disabled={!isInbStIgnOvrflwdCallsWithinEnabled}
                    adornmentEnd={language.get(
                        InboundStatSettingsLanguage.InboundStatSettings_IgnoreovrflwdcallswithinUnit
                    )}
                />
            </FormRow>
            <FormRow isInline className='ignore-inbound-escaped-calls'>
                <Checkbox
                    labelRight={language.get(InboundStatSettingsLanguage.InboundStatSettings_IgnoreEscapedCalls)}
                    isChecked={isInboundEscapedCallsIgnored}
                    onChange={(val: boolean) => setIsInboundEscapedCallsIgnored(val)}
                />
            </FormRow>
        </Fieldset>
    )
}

export default React.memo(InboundStatSettings)
